import React, { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Check, Loader2 } from 'lucide-react'
import { useLocale } from '../contexts/LocaleContext'
import { api, slugify, SignupResult } from '../api'
import { PORTAL_URL } from '../config'
import Header from '../components/Header'
import Footer from '../components/Footer'
import TransitionBanner from '../components/TransitionBanner'

type SlugState = 'idle' | 'checking' | 'available' | 'taken' | 'invalid'

const field =
  'min-h-touch-comfort w-full rounded-md border border-border bg-surface-card px-3.5 text-base text-text-primary placeholder:text-text-muted'
const labelStyle = 'text-sm font-semibold text-text-primary'

/**
 * Organisation signup. The subdomain is the one irreversible choice here, so it is
 * suggested from the name, shown as a full address, and checked while they type.
 */
const SignUpPage: React.FC = () => {
  const { t } = useLocale()
  const [params] = useSearchParams()

  const [organizationName, setOrganizationName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugEdited, setSlugEdited] = useState(false)
  const [slugState, setSlugState] = useState<SlugState>('idle')

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState(params.get('email') ?? '')
  const [password, setPassword] = useState('')
  const [accepted, setAccepted] = useState(false)

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<SignupResult | null>(null)

  useEffect(() => {
    if (!slugEdited) setSlug(slugify(organizationName))
  }, [organizationName, slugEdited])

  // Debounced so a fast typist does not fire a request per keystroke, and so the last
  // answer shown always belongs to the text currently in the box.
  useEffect(() => {
    if (!slug) {
      setSlugState('idle')
      return
    }
    setSlugState('checking')
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const res = await api.checkSlug(slug)
        if (cancelled) return
        setSlugState(!res.valid ? 'invalid' : res.available ? 'available' : 'taken')
      } catch {
        if (!cancelled) setSlugState('idle')
      }
    }, 400)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [slug])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!accepted) {
      setError(t('signup.acceptTermsRequired'))
      return
    }
    setSubmitting(true)
    setError('')
    try {
      setResult(
        await api.signup({
          organization_name: organizationName.trim(),
          slug,
          email: email.trim(),
          password,
          first_name: firstName.trim(),
          last_name: lastName.trim() || undefined,
          accept_terms: true,
          accept_privacy: true,
        }),
      )
    } catch (err: any) {
      setError(err.message || 'Er ging iets mis.')
    } finally {
      setSubmitting(false)
    }
  }

  const [beforeTerms, rest] = String(t('signup.acceptTerms')).split('{terms}')
  const [betweenLinks, afterPrivacy] = (rest ?? '').split('{privacy}')

  return (
    <div className="min-h-screen">
      <TransitionBanner />
      <Header />

      <main className="mx-auto max-w-[560px] px-5 py-14 sm:px-8">
        {result ? (
          <div className="rounded-lg border border-border bg-surface-card p-7 shadow-card">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-status-success-bg text-status-success-text">
              <Check className="h-5 w-5" />
            </div>
            <h1 className="mt-4 font-display text-[28px] font-bold tracking-display">
              {t('signup.successTitle')}
            </h1>
            <p className="mt-2 text-md text-text-secondary">{t('signup.successBody')}</p>
            {result.confirm_url && (
              <div className="mt-5 rounded-md bg-status-warning-bg px-4 py-3 text-md text-status-warning-text">
                E-mail staat niet aan op deze server. Bevestig je account via:
                <a
                  href={result.confirm_url}
                  className="mt-1 block break-all font-semibold underline underline-offset-2"
                >
                  {result.confirm_url}
                </a>
              </div>
            )}
            <a
              href={result.portal_url}
              className="mt-6 inline-flex min-h-touch-comfort items-center rounded-md bg-action-primary px-7 font-semibold text-text-on-primary hover:bg-action-primary-hover"
            >
              {t('signup.goToPortal')}
            </a>
          </div>
        ) : (
          <>
            <h1 className="font-display text-[32px] font-bold tracking-display">
              {t('signup.title')}
            </h1>
            <p className="mt-2 text-[17px] text-text-secondary">{t('signup.subtitle')}</p>

            {error && (
              <p
                role="alert"
                className="mt-5 rounded-md bg-status-danger-bg px-4 py-3 text-md text-status-danger-text"
              >
                {error}
              </p>
            )}

            <form onSubmit={submit} className="mt-7 flex flex-col gap-5">
              <div className="flex flex-col gap-1.5">
                <label className={labelStyle} htmlFor="org-name">
                  {t('signup.organizationName')}
                </label>
                <input
                  id="org-name"
                  required
                  className={field}
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                />
                <span className="text-sm text-text-muted">
                  {t('signup.organizationNameHint')}
                </span>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={labelStyle} htmlFor="org-slug">
                  {t('signup.slug')}
                </label>
                <div className="flex items-center gap-1 rounded-md border border-border bg-surface-card px-3.5">
                  <input
                    id="org-slug"
                    required
                    className="min-h-touch-comfort min-w-0 flex-1 bg-transparent text-base text-text-primary outline-none"
                    value={slug}
                    onChange={(e) => {
                      setSlugEdited(true)
                      setSlug(slugify(e.target.value))
                    }}
                  />
                  <span className="shrink-0 text-base text-text-muted">.rallyshift.io</span>
                  {slugState === 'checking' && (
                    <Loader2 className="h-4 w-4 shrink-0 animate-rs-spin text-text-muted" />
                  )}
                  {slugState === 'available' && (
                    <Check className="h-4 w-4 shrink-0 text-status-success" />
                  )}
                </div>
                <span
                  className={
                    slugState === 'taken' || slugState === 'invalid'
                      ? 'text-sm text-status-danger-text'
                      : 'text-sm text-text-muted'
                  }
                >
                  {slugState === 'taken'
                    ? t('signup.slugTaken')
                    : slugState === 'invalid'
                      ? t('signup.slugInvalid')
                      : t('signup.slugHint')}
                </span>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <label className={labelStyle} htmlFor="first-name">
                    {t('signup.yourName')}
                  </label>
                  <input
                    id="first-name"
                    required
                    className={field}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className={labelStyle} htmlFor="last-name">
                    {t('common.lastName')}
                  </label>
                  <input
                    id="last-name"
                    className={field}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={labelStyle} htmlFor="email">
                  {t('signup.email')}
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  className={field}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={labelStyle} htmlFor="password">
                  {t('signup.password')}
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className={field}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <span className="text-sm text-text-muted">{t('signup.passwordHint')}</span>
              </div>

              <label className="flex items-start gap-3 text-md text-text-secondary">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-1 h-5 w-5 shrink-0 accent-clay-500"
                />
                <span>
                  {beforeTerms}
                  <Link to="/terms-of-service" className="font-semibold text-action-primary underline">
                    {t('footer.termsOfService')}
                  </Link>
                  {betweenLinks}
                  <Link to="/privacy-policy" className="font-semibold text-action-primary underline">
                    {t('footer.privacyPolicy')}
                  </Link>
                  {afterPrivacy}
                </span>
              </label>

              <button
                type="submit"
                disabled={submitting || slugState === 'taken' || slugState === 'invalid'}
                className="inline-flex min-h-touch-comfort items-center justify-center rounded-md bg-action-primary px-7 text-[17px] font-semibold text-text-on-primary hover:bg-action-primary-hover disabled:opacity-60"
              >
                {submitting ? t('signup.submitting') : t('signup.submit')}
              </button>

              <p className="text-center text-md text-text-secondary">
                {t('signup.haveAccount')}{' '}
                <a href={PORTAL_URL} className="font-semibold text-action-primary underline">
                  {t('signup.login')}
                </a>
              </p>
            </form>
          </>
        )}
      </main>

      <Footer />
    </div>
  )
}

export default SignUpPage
